import { type TGlobalFunctionTyped } from 'common/hooks/useTranslation';
import { downloadFromBlob } from 'src/core/utils/fileDownloader';

type ExportAsCsvParams<ItemType> = {
  items: ItemType[];
  columns: ExportAsCsvColumn<ItemType>[];
  translator: TGlobalFunctionTyped;
  fileName?: string;
  csvSeparator?: string;
};

type ExportAsCsvFunction = <ItemType extends object>(
  exportParams: ExportAsCsvParams<ItemType>,
) => void;

export type ExportAsCsvColumn<ItemType> = {
  value(item: ItemType, translator: TGlobalFunctionTyped): string | undefined;
  title(translator: TGlobalFunctionTyped): string;
};

export const exportAsCsv: ExportAsCsvFunction = ({
  columns,
  items,
  translator,
  csvSeparator = ',',
  fileName = 'export.csv',
}) => {
  const exportContent = prepareCsv({
    columns,
    items,
    translator,
    csvSeparator,
    fileName,
  });
  return download(exportContent, fileName);
};

type PrepareCsvFunction = <ItemType extends object>(
  exportParams: ExportAsCsvParams<ItemType>,
) => string;

export const buildCsvCell = (content: string): string =>
  `"${content.replaceAll('"', '""')}"`;

export const prepareCsv: PrepareCsvFunction = ({
  columns,
  items,
  translator,
  csvSeparator = ',',
}) => {
  const header = columns
    .reduce<
      string[]
    >((titles, column) => [...titles, column.title(translator)], [])
    .join(csvSeparator);

  const rows = items.flatMap((item) => {
    return columns
      .reduce<string[]>((values, column) => {
        const newValue = column.value(item, translator) ?? '';
        return [...values, buildCsvCell(newValue)];
      }, [])
      .join(csvSeparator);
  });

  return [header, ...rows].join('\n');
};

function download(exportContent: string, fileName: string): void {
  const blob = new Blob([exportContent], { type: 'text/csv' });
  return downloadFromBlob(blob, fileName);
}
